// The factory's progress panel on the supplier portal's Purchase Order page.
//
// Why this is JS and not a template
// ---------------------------------
// The page is core's `erpnext/templates/pages/order.html`, reached through
// erpnext's own `website_route_rules`. Forking that template to add a panel
// would mean maintaining a copy of a core file that changes on every ERPNext
// release, and a route override would mean owning the whole page. So the panel
// is appended to the rendered page instead -- extend by wrapping, never fork.
//
// It is inert almost everywhere by construction: it does nothing unless the URL
// is a purchase-order page, and then only if the server says this site has the
// feature. `get_progress` answers `{enabled: false}` rather than throwing on a
// site that does not, precisely so this file can ask on every site without
// writing an error-log row each time somebody opens an order.
//
// Nothing here decides anything. The stage list, their order, which are reached
// and which may be reported next all arrive from the server; this file paints
// them and posts back a choice. That is deliberate -- an outside party reads
// this panel, and a browser-side copy of the rule would eventually disagree
// with the rule.

(() => {
	// Mirrors api/factory_stage.SHIPPED. The only stage name this file needs to
	// know, because it is the one the shipping fields hang off; every other stage
	// arrives from the server as opaque text.
	const SHIPPED = "Shipped";

	const ROUTE = /^\/purchase-orders\/([^/?#]+)/;
	const READ = "servos_operations.api.factory_portal.get_progress";
	const WRITE = "servos_operations.api.factory_portal.report_stage";
	const TICKETS = "servos_operations.api.factory_labels.label_count";
	// A download must be a plain URL: the browser saves the file itself, which
	// `frappe.call` cannot do -- it would hand back a PDF as a JSON string.
	const TICKET_SHEET = "/api/method/servos_operations.api.factory_labels.download_label_sheet";

	const match = window.location.pathname.match(ROUTE);
	if (!match || typeof frappe === "undefined" || !frappe.call) {
		return;
	}
	const order = decodeURIComponent(match[1]);

	// Styles ride with the panel rather than a stylesheet in `web_include_css`:
	// that hook loads on every website page of every site in the bench group,
	// and this CSS is wanted on exactly one page of one vertical.
	const CSS = `
.fp-wrap { display: grid; grid-template-columns: minmax(0, 264px) minmax(0, 1fr); gap: 16px; align-items: start; margin: 24px 0 8px; }
@media (max-width: 780px) { .fp-wrap { grid-template-columns: minmax(0, 1fr); } }
.fp-card { background: #fff; border: 1px solid var(--servos-line, #e3ebf4); border-radius: var(--site-radius-md, 14px); padding: 18px 20px 20px; box-shadow: var(--servos-shadow-sm, 0 1px 2px rgba(28,43,58,.06), 0 1px 3px rgba(28,43,58,.08)); }
.fp-title { font-size: 15px; font-weight: 700; letter-spacing: -0.01em; margin: 0; color: var(--servos-text-dark, #1c2b3a); }
.fp-sub { font-size: 12px; color: var(--servos-text-mid, #7b8fa1); margin: 4px 0 16px; line-height: 1.45; }
.fp-steps { display: flex; flex-direction: column; }
.fp-step { display: flex; align-items: flex-start; gap: 12px; }
.fp-dot { width: 26px; height: 26px; border-radius: 999px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; line-height: 1; }
.fp-dot--done { background: #e3f3e8; color: #1f8a4c; }
.fp-dot--now { background: var(--servos-action, #1a6fc4); color: #fff; box-shadow: 0 0 0 4px color-mix(in srgb, var(--servos-action, #1a6fc4) 16%, transparent); }
.fp-dot--todo { border: 1.5px dashed var(--servos-line, #d6e0ec); background: #fff; }
.fp-dot--skipped { background: var(--servos-hover, #f2f6fa); border: 1px solid var(--servos-line, #e3ebf4); }
.fp-rail { width: 1.5px; height: 22px; background: var(--servos-line, #e3ebf4); margin-inline-start: 12px; }
.fp-step-name { font-size: 13.5px; font-weight: 600; color: var(--servos-text-dark, #1c2b3a); }
.fp-step-name--muted { font-weight: 500; color: var(--servos-text-mid, #7b8fa1); }
.fp-step-at { font-size: 11.5px; color: var(--servos-text-mid, #7b8fa1); margin-top: 2px; }
.fp-fields { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin-bottom: 16px; }
@media (max-width: 560px) { .fp-fields { grid-template-columns: minmax(0, 1fr); } }
.fp-label { display: block; font-size: 11.5px; font-weight: 600; color: var(--servos-text-mid, #7b8fa1); margin-bottom: 5px; }
.fp-input { width: 100%; height: 38px; border: 1px solid var(--servos-line, #d6e0ec); border-radius: var(--site-radius-sm, 8px); padding: 0 11px; font-size: 14px; font-family: inherit; color: var(--servos-text-dark, #1c2b3a); background: #fff; }
.fp-input:focus { border-color: var(--servos-action, #1a6fc4); box-shadow: 0 0 0 3px color-mix(in srgb, var(--servos-action, #1a6fc4) 18%, transparent); outline: none; }
.fp-value { font-size: 14px; font-weight: 600; color: var(--servos-text-dark, #1c2b3a); }
.fp-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.fp-btn { height: 40px; padding: 0 18px; border-radius: var(--site-radius-sm, 8px); border: 1px solid var(--servos-line, #d6e0ec); background: #fff; color: var(--servos-text-dark, #1c2b3a); font-size: 14px; font-weight: 500; font-family: inherit; cursor: pointer; transition: border-color .14s ease, box-shadow .14s ease; }
.fp-ticket-count { margin: 0 0 14px; font-size: 26px; font-weight: 700; letter-spacing: -0.02em; color: var(--servos-text-dark, #1c2b3a); }
.fp-btn:hover:not([disabled]) { border-color: var(--servos-action, #1a6fc4); }
.fp-btn--primary { background: var(--servos-action, #1a6fc4); border-color: var(--servos-action, #1a6fc4); color: #fff; font-weight: 600; box-shadow: 0 1px 2px color-mix(in srgb, var(--servos-action, #1a6fc4) 35%, transparent); }
.fp-btn--primary:hover:not([disabled]) { background: var(--servos-secondary, #1a6fc4); border-color: var(--servos-secondary, #1a6fc4); }
.fp-btn[disabled] { opacity: .55; cursor: default; }
.fp-note { display: flex; align-items: flex-start; gap: 9px; margin-top: 16px; padding: 11px 13px; border-radius: var(--site-radius-sm, 8px); background: var(--subtle-fg, #f7fafd); border: 1px solid var(--servos-line, #e3ebf4); font-size: 12.5px; color: var(--servos-text-mid, #7b8fa1); line-height: 1.5; }
.fp-done { font-size: 13px; color: var(--servos-text-mid, #7b8fa1); line-height: 1.5; margin: 0; }
`;

	function injectStyles() {
		if (document.getElementById("fp-styles")) return;
		const tag = document.createElement("style");
		tag.id = "fp-styles";
		tag.textContent = CSS;
		document.head.appendChild(tag);
	}

	// Core markup, so it is read defensively: try the most specific anchor first
	// and fall back outward. If none of them match after an ERPNext upgrade the
	// panel simply does not appear -- it must never break her order page.
	function anchor() {
		// `.order-container` wraps the items and totals and is always present on
		// this page. `.order-item-table` is NOT -- it looks like the items wrapper
		// in order.html but only renders inside its `{% if attachments %}` block,
		// so on an order with no attachments it does not exist. Read off the live
		// DOM rather than the template.
		const container = document.querySelector(".order-container");
		if (container) return { el: container, how: "after" };
		const tables = document.querySelectorAll(".order-item-table");
		if (tables.length) return { el: tables[tables.length - 1], how: "after" };
		const indicator = document.querySelector(".indicator-container");
		if (indicator && indicator.parentElement) return { el: indicator.parentElement, how: "append" };
		const page = document.querySelector(".page_content, .page-content, main");
		return page ? { el: page, how: "append" } : null;
	}

	// `__` comes from frappe's translate bundle. It is present on website pages
	// today, but this panel must degrade to English rather than die with a
	// ReferenceError if that ever stops being true on a customer's portal.
	const t = (s) => (typeof __ === "function" ? __(s) : s);

	function el(tag, className, text) {
		const node = document.createElement(tag);
		if (className) node.className = className;
		if (text !== undefined) {
			node.textContent = text;
			// `dir="auto"` per text node, because this panel renders inside a page
			// whose direction belongs to the READER, not to these strings.
			//
			// Frappe sets `dir` on <html> from the current language
			// (`templates/base.html`), and for a logged-in user that comes from
			// `User.language` (`translate.get_language`, priority 4). A factory
			// contact whose language is unset inherits the site's -- Hebrew for an
			// Israeli customer -- so an English sentence rendered RTL puts its full
			// stop at the wrong end: ".is not yours to set". Seen exactly that way
			// on a live site.
			//
			// `auto` asks the browser to infer direction from each element's own
			// first strong character, so English reads LTR and a translated Hebrew
			// string reads RTL, in either page. Setting `direction: ltr` on the card
			// instead would fix today's English and break tomorrow's translation.
			node.setAttribute("dir", "auto");
		}
		return node;
	}

	function when(value) {
		if (!value) return "";
		// frappe.datetime is present on website pages; fall back to the raw value
		// rather than showing nothing.
		try {
			return frappe.datetime.str_to_user(String(value));
		} catch (e) {
			return String(value);
		}
	}

	function renderSteps(timeline) {
		const wrap = el("div", "fp-steps");
		timeline.forEach((row, i) => {
			const step = el("div", "fp-step");

			// Four states, not three. A stage BEHIND the current one that carries no
			// timestamp was never reported -- she skipped it, which the server
			// allows on purpose. Painting it with a tick would claim she said
			// something she did not; the panel is the record, so it says "not
			// reported" instead of quietly inventing a completion.
			let state;
			if (row.current) {
				state = "now";
			} else if (row.reached) {
				state = row.at ? "done" : "skipped";
			} else {
				state = "todo";
			}

			const dot = el("div", `fp-dot fp-dot--${state}`);
			if (state === "done") dot.textContent = "✓";
			step.appendChild(dot);

			const meta = el("div");
			const dim = state === "todo" || state === "skipped";
			meta.appendChild(el("div", `fp-step-name${dim ? " fp-step-name--muted" : ""}`, t(row.stage)));
			const at = when(row.at);
			if (at) {
				meta.appendChild(el("div", "fp-step-at", at));
			} else if (state === "skipped") {
				meta.appendChild(el("div", "fp-step-at", t("not reported")));
			}
			step.appendChild(meta);

			wrap.appendChild(step);
			if (i < timeline.length - 1) wrap.appendChild(el("div", "fp-rail"));
		});
		return wrap;
	}

	// Once she is done reporting, the panel should still show what she said --
	// the ship date and container are the useful part of the record.
	function shipSummary(state) {
		const rows = [
			[t("Ship date"), state.shipped_on ? when(state.shipped_on) : ""],
			[t("Forwarder"), state.forwarder],
			[t("Container / AWB"), state.tracking],
		].filter(([, value]) => value);
		if (!rows.length) return null;

		const wrap = el("div", "fp-fields");
		wrap.style.marginTop = "14px";
		rows.forEach(([label, value]) => {
			const cell = el("div");
			cell.appendChild(el("div", "fp-label", label));
			cell.appendChild(el("div", "fp-value", String(value)));
			wrap.appendChild(cell);
		});
		return wrap;
	}

	/** The ticket sheet card.
	 *
	 * Asked for separately from the progress read, and drawn only once the
	 * count is back: "Download tickets" with no number beside it does not tell
	 * her whether she is about to wait for 4 or for 400.
	 */
	function renderTickets(mount) {
		const card = el("div", "fp-card");
		card.appendChild(el("h3", "fp-title", t("Item tickets")));
		const sub = el("p", "fp-sub", t("Counting the tickets for this order..."));
		card.appendChild(sub);
		mount.appendChild(card);

		frappe.call({ method: TICKETS, args: { purchase_order: order } }).then((r) => {
			const info = (r && r.message) || {};
			if (!info.enabled) {
				card.remove();
				return;
			}
			if (!info.labels) {
				sub.textContent = t("This order has nothing to print tickets for.");
				return;
			}

			sub.textContent = t("One ticket per item, to attach to each piece.");
			const count = el(
				"p",
				"fp-ticket-count",
				`${info.labels} ${info.labels === 1 ? t("ticket") : t("tickets")}`
			);
			card.appendChild(count);

			if (info.too_many) {
				card.appendChild(
					el("p", "fp-done", t("Too many tickets for one sheet. Ask Etcasiart to split the order."))
				);
				return;
			}

			if (!info.ready) {
				// The sheet is rendered and attached when Etcasiart places the
				// order. If it is not there, no button: a download that fails is
				// worse than one that is honestly absent.
				card.appendChild(
					el("p", "fp-done", t("The tickets are not ready yet. Etcasiart attaches them to the order."))
				);
				return;
			}

			const btn = el("button", "fp-btn fp-btn--primary", t("Download tickets (PDF)"));
			btn.type = "button";
			btn.addEventListener("click", () => {
				btn.disabled = true;
				btn.textContent = t("Preparing...");
				// Navigating to the URL lets the browser save the file. It does not
				// leave the page: the response is a download, so the order page
				// stays where it is.
				window.location.href = `${TICKET_SHEET}?purchase_order=${encodeURIComponent(order)}`;
				window.setTimeout(() => {
					btn.disabled = false;
					btn.textContent = t("Download tickets (PDF)");
				}, 4000);
			});
			card.appendChild(btn);
		});
	}

	function render(state, mount) {
		mount.textContent = "";
		injectStyles();

		// Two cards, as designed: where the order is, and what she can say about
		// it. One card with a divider read as a debug panel bolted to the page.
		const progress = el("div", "fp-card");
		progress.appendChild(el("h3", "fp-title", t("Progress")));
		progress.appendChild(el("p", "fp-sub", t("Where this order has got to.")));
		progress.appendChild(renderSteps(state.timeline || []));
		mount.appendChild(progress);

		renderTickets(mount);

		const right = el("div", "fp-card");
		right.appendChild(el("h3", "fp-title", t("Update Etcasiart")));
		right.appendChild(
			el(
				"p",
				"fp-sub",
				t("What you record here is the record. Your name and the date are saved with it.")
			)
		);
		const nexts = state.next_stages || [];

		if (!nexts.length) {
			right.appendChild(
				el("p", "fp-done", t("Nothing further to report. Etcasiart records arrival when the goods are counted in."))
			);
			const shipped = shipSummary(state);
			if (shipped) right.appendChild(shipped);
		} else {
			// The shipping details belong to exactly one action, so they are only
			// on screen when that action is the expected next step. Showing a ship
			// date next to a "Production Complete" button invites her to fill in a
			// date for goods still being cut -- which is what the first version of
			// this panel did, and it looked wrong the moment it was on screen.
			const shipIsNext = nexts[0] === SHIPPED;
			let shipInputs = null;
			let fields = null;
			if (nexts.includes(SHIPPED)) {
				fields = el("div", "fp-fields");
				if (!shipIsNext) fields.style.display = "none";
				shipInputs = {};
				[
					["shipped_on", t("Ship date"), "date"],
					["forwarder", t("Forwarder"), "text"],
					["tracking", t("Container / AWB"), "text"],
				].forEach(([key, label, type]) => {
					const cell = el("div");
					const lab = el("label", "fp-label", label);
					lab.setAttribute("for", `fp-${key}`);
					const input = el("input", "fp-input");
					input.type = type;
					input.id = `fp-${key}`;
					if (state[key]) {
						input.value = type === "date" ? String(state[key]).slice(0, 10) : state[key];
					}
					shipInputs[key] = input;
					cell.appendChild(lab);
					cell.appendChild(input);
					fields.appendChild(cell);
				});
				right.appendChild(fields);
			}

			const actions = el("div", "fp-actions");
			nexts.forEach((stage, i) => {
				const btn = el("button", `fp-btn${i === 0 ? " fp-btn--primary" : ""}`, t(stage));
				btn.type = "button";

				// Reporting Shipped out of turn is legal (the server allows forward
				// skips) but it is a three-stage jump that also wants a date, so it
				// asks first: one click reveals the fields, the second commits.
				let armed = stage !== SHIPPED || shipIsNext;
				btn.addEventListener("click", () => {
					if (!armed) {
						armed = true;
						if (fields) {
							fields.style.display = "";
							if (shipInputs.shipped_on) shipInputs.shipped_on.focus();
						}
						btn.textContent = t("Confirm shipped");
						btn.classList.add("fp-btn--primary");
						return;
					}
					submit(stage, shipInputs, mount, actions);
				});
				actions.appendChild(btn);
			});
			right.appendChild(actions);
		}

		const note = el("div", "fp-note");
		note.appendChild(
			el(
				"span",
				null,
				t("Your reports end at Shipped. Arrival is recorded by Etcasiart when the goods are counted in, so it is not yours to set.")
			)
		);
		right.appendChild(note);

		mount.appendChild(right);
	}

	function submit(stage, shipInputs, mount, actions) {
		Array.from(actions.querySelectorAll("button")).forEach((b) => {
			b.disabled = true;
		});
		const args = { purchase_order: order, stage: stage };
		if (shipInputs && stage === "Shipped") {
			["shipped_on", "forwarder", "tracking"].forEach((key) => {
				const value = (shipInputs[key].value || "").trim();
				if (value) args[key] = value;
			});
		}
		frappe.call({ method: WRITE, args: args })
			.then((r) => {
				if (r && r.message) render(r.message, mount);
			})
			.catch(() => {
				// frappe.call already shows the server's message; re-enable so a
				// corrected attempt is possible without a reload.
				Array.from(actions.querySelectorAll("button")).forEach((b) => {
					b.disabled = false;
				});
			});
	}

	frappe.call({ method: READ, args: { purchase_order: order } }).then((r) => {
		const state = r && r.message;
		if (!state || !state.enabled) return;

		const spot = anchor();
		if (!spot) return;

		const mount = el("div", "fp-wrap");
		mount.id = "factory-progress";
		if (spot.how === "after") {
			spot.el.insertAdjacentElement("afterend", mount);
		} else {
			spot.el.appendChild(mount);
		}
		render(state, mount);
	});
})();
